/**
 * Tenant2FASetup — wizard konfiguracji 2FA dla usera tenantowego (F7b T7b.5).
 *
 * Adaptacja `Admin2FASetup.tsx` (F6 T6.7) do endpointów tenantowych (F5 T5.11):
 *  - `POST /api/auth/2fa/setup` (Bearer access z TenantAuthContext) → `{secret, qrDataUrl, backupCodes}`.
 *  - `POST /api/auth/2fa/verify` → `{enabled:true}`.
 *
 * Różnice wobec Admin2FASetup:
 *  - Auth: standardowy Bearer access (api interceptor z T7b.1), nie setupToken z location.state.
 *    Admin ma setupToken bo 2FA jest force-init przed normalnym loginiem.
 *    Tenant jest już zalogowany (2FA opt-in per D8).
 *  - Po sukcesie: nie ma Set-Cookie (już zalogowany) — wymaga jawnej akcji:
 *    `refreshMe()` (invalidate /me → badge totpEnabled=true) + toast + navigate('/profile').
 *  - Error 2FA_ALREADY_ENABLED (409) → inline error + link /profile (shouldn't happen bo
 *    MyProfile CTA pokazuje się tylko gdy totpEnabled=false, ale defensywnie).
 *
 * State machine: identyczna z Admin2FASetup — `loading-setup | display | verify |
 * submitting-verify | error`.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface SetupResponse {
  secret: string;
  qrDataUrl: string;
  backupCodes: string[];
}

type Step =
  | { kind: 'loading-setup' }
  | { kind: 'display'; secret: string; qrDataUrl: string; backupCodes: string[] }
  | { kind: 'verify'; secret: string; backupCodes: string[] }
  | { kind: 'submitting-verify'; secret: string; backupCodes: string[] }
  | { kind: 'error'; message: string };

export function Tenant2FASetup() {
  const { t } = useTranslation();
  const { refreshMe } = useTenantAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>({ kind: 'loading-setup' });
  const [totpCode, setTotpCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.post<SetupResponse>('/auth/2fa/setup', {});
        setStep({
          kind: 'display',
          secret: data.secret,
          qrDataUrl: data.qrDataUrl,
          backupCodes: data.backupCodes,
        });
      } catch (err) {
        const e = err as { response?: { status?: number; data?: { error?: string } } };
        const status = e.response?.status;
        const code = e.response?.data?.error;
        let msg = t('tenant.2fa.errors.setupGeneric');
        if (status === 409 && code === 'ALREADY_ENABLED') {
          msg = t('tenant.2fa.errors.alreadyEnabled');
        } else if (status === 401) {
          msg = t('tenant.2fa.errors.unauthorized');
        }
        setStep({ kind: 'error', message: msg });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownloadBackupCodes = () => {
    if (step.kind !== 'display') return;
    const content = [
      t('tenant.2fa.download.header'),
      `${t('tenant.2fa.download.generated')}: ${new Date().toISOString()}`,
      '',
      t('tenant.2fa.download.warning'),
      '',
      ...step.backupCodes,
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `saas-2fa-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onProceedToVerify = () => {
    if (step.kind !== 'display') return;
    setStep({ kind: 'verify', secret: step.secret, backupCodes: step.backupCodes });
  };

  const onSubmitVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (step.kind !== 'verify') return;
    setVerifyError(null);
    const current = step;
    setStep({
      kind: 'submitting-verify',
      secret: current.secret,
      backupCodes: current.backupCodes,
    });
    try {
      await api.post('/auth/2fa/verify', {
        secret: current.secret,
        totpCode,
        backupCodes: current.backupCodes,
      });
      await refreshMe();
      toast.show({ variant: 'success', title: t('tenant.2fa.toastEnabled') });
      navigate('/profile', { replace: true });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setVerifyError(
        status === 401 ? t('tenant.2fa.errors.invalidTotp') : t('tenant.2fa.errors.verifyGeneric'),
      );
      setStep(current);
    }
  };

  if (step.kind === 'loading-setup') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-text-tertiary">{t('tenant.2fa.loadingSetup')}</p>
        </div>
      </main>
    );
  }

  if (step.kind === 'error') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
          <h1 className="mb-4 text-xl font-bold text-text-primary">
            {t('tenant.2fa.errorHeading')}
          </h1>
          <p className="mb-6 text-sm text-text-secondary" role="alert">
            {step.message}
          </p>
          <Button className="w-full" onClick={() => navigate('/profile')}>
            {t('tenant.2fa.backToProfile')}
          </Button>
        </div>
      </main>
    );
  }

  if (step.kind === 'display') {
    return (
      <main className="flex min-h-screen items-start justify-center bg-bg-secondary p-4 py-8">
        <div className="w-full max-w-2xl rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-text-primary">{t('tenant.2fa.title')}</h1>
            <p className="mt-2 text-sm text-text-tertiary">{t('tenant.2fa.subtitle')}</p>
          </div>

          <div className="mb-6 rounded-lg border border-border-secondary bg-bg-secondary p-4">
            <h2 className="mb-3 font-semibold text-text-primary">{t('tenant.2fa.step1Heading')}</h2>
            <p className="mb-3 text-sm text-text-tertiary">{t('tenant.2fa.step1Description')}</p>
            <div className="flex justify-center rounded-lg bg-white p-4">
              <img
                src={step.qrDataUrl}
                alt={t('tenant.2fa.qrAlt')}
                className="h-48 w-48"
                data-testid="qr-image"
              />
            </div>
            <p className="mt-3 text-xs text-text-muted">
              {t('tenant.2fa.manualSecretHint')}{' '}
              <code
                data-testid="totp-secret"
                className="rounded bg-bg-tertiary px-2 py-0.5 font-mono text-xs text-text-primary"
              >
                {step.secret}
              </code>
            </p>
          </div>

          <div className="mb-6 rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
            <h2 className="mb-3 font-semibold text-text-primary">{t('tenant.2fa.step2Heading')}</h2>
            <p
              className="mb-3 text-sm text-text-secondary"
              dangerouslySetInnerHTML={{ __html: t('tenant.2fa.step2Description') }}
            />
            <div
              className="mb-3 grid grid-cols-2 gap-2 font-mono text-sm"
              data-testid="backup-codes-list"
            >
              {step.backupCodes.map((code) => (
                <div key={code} className="rounded bg-bg-elevated px-3 py-2 text-text-primary">
                  {code}
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={onDownloadBackupCodes}
              className="w-full"
            >
              {t('tenant.2fa.downloadBtn')}
            </Button>
          </div>

          <Button className="w-full" onClick={onProceedToVerify}>
            {t('tenant.2fa.proceedBtn')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="mt-3 w-full"
            onClick={() => navigate('/profile')}
          >
            {t('tenant.2fa.backToProfile')}
          </Button>
        </div>
      </main>
    );
  }

  // step.kind === 'verify' | 'submitting-verify'
  const loading = step.kind === 'submitting-verify';
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <h1 className="mb-2 text-2xl font-bold text-text-primary">
          {t('tenant.2fa.step3Heading')}
        </h1>
        <p className="mb-6 text-sm text-text-tertiary">{t('tenant.2fa.step3Description')}</p>

        <form onSubmit={onSubmitVerify} className="space-y-4" noValidate>
          <Input
            label={t('tenant.2fa.totpCodeLabel')}
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            required
            autoFocus
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
            placeholder={t('tenant.2fa.totpCodePlaceholder')}
            disabled={loading}
          />

          {verifyError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {verifyError}
            </div>
          )}

          <Button type="submit" className="w-full" loading={loading}>
            {loading ? t('tenant.2fa.verifyingBtn') : t('tenant.2fa.verifyBtn')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => navigate('/profile')}
            disabled={loading}
          >
            {t('tenant.2fa.backToProfile')}
          </Button>
        </form>
      </div>
    </main>
  );
}
